import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import "./VendorRateForm.css";

const BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://api.quote.softvolute.in";

const money = (value) => `₹${Number(value || 0).toFixed(2)}`;

const VendorRateForm = () => {
  const { token } = useParams();

  const [estimate, setEstimate] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitted, setSubmitted] = useState(false);

  const totalAmount = useMemo(
    () => items.reduce((sum, item) => sum + Number(item.vendorAmount || 0), 0),
    [items],
  );

  const fetchForm = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/vendor-rate-form/${token}`);

      setEstimate(res.data);
      setItems(res.data.items || []);

      if (res.data.status === "SUBMITTED") {
        setSubmitted(true);
      }
    } catch (err) {
      console.error("Vendor form fetch error:", err?.response?.data || err);
      alert(err?.response?.data?.message || "Invalid or expired form link.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const updateItem = (index, field, value) => {
    const updated = [...items];

    updated[index][field] = value;

    if (field === "vendorRate") {
      updated[index].vendorAmount =
        Number(value || 0) * Number(updated[index].qty || 0);
    }

    setItems(updated);
  };

  const submitRates = async () => {
    try {
      const hasEmptyRate = items.some((i) => !i.vendorRate);

      if (hasEmptyRate) {
        alert("Please enter rate for all items.");
        return;
      }

      await axios.post(`${BASE_URL}/api/vendor-rate-form/${token}/submit`, {
        items,
        totalAmount,
      });

      setSubmitted(true);
      alert("Rates submitted successfully.");
    } catch (err) {
      console.error("Submit rates error:", err?.response?.data || err);
      alert(err?.response?.data?.message || "Failed to submit rates.");
    }
  };

  if (loading) return <div className="vr-state">Loading...</div>;

  if (!estimate) return <div className="vr-state">Form not found.</div>;

  return (
    <div className="vr-page">
      <div className="vr-card">
        <h2 className="vr-title">Vendor Rate Submission Form</h2>

        <div className="vr-info mb-3">
          <b>Project:</b> {estimate.projectName}
          <br />
          <b>Vendor:</b> {estimate.vendorName}
          <br />
          <b>Vendor Code:</b> {estimate.vendorCode || "-"}
        </div>

        {submitted && (
          <div className="alert alert-success fw-bold">
            This form has already been submitted.
          </div>
        )}

        <div className="vr-table-wrap">
          <table className="table table-bordered table-striped vr-table">
            <thead className="table-secondary text-center">
              <tr>
                <th>Sr No</th>
                <th>Item</th>
                <th>Description</th>
                <th>Qty</th>
                <th>Area</th>
                <th>Your Rate</th>
                <th>Amount</th>
                <th>Remark</th>
              </tr>
            </thead>

            <tbody>
              {items.map((row, index) => (
                <tr key={index} className="vr-row">
                  <td className="text-center vr-c-sr">{index + 1}</td>
                  <td className="vr-c-item">{row.itemName || "-"}</td>
                  <td className="vr-c-desc">{row.description || "-"}</td>
                  <td className="text-center" data-label="Qty">
                    {row.qty || 0}
                  </td>
                  <td data-label="Area">{row.area || "-"}</td>

                  <td className="vr-c-rate" data-label="Your Rate">
                    <input
                      type="number"
                      inputMode="decimal"
                      className="form-control"
                      value={row.vendorRate || ""}
                      disabled={submitted}
                      onChange={(e) =>
                        updateItem(index, "vendorRate", e.target.value)
                      }
                    />
                  </td>

                  <td data-label="Amount">
                    <input
                      className="form-control fw-bold"
                      value={row.vendorAmount ? money(row.vendorAmount) : ""}
                      readOnly
                    />
                  </td>

                  <td className="vr-c-remark" data-label="Remark">
                    <input
                      className="form-control"
                      value={row.vendorRemark || ""}
                      disabled={submitted}
                      onChange={(e) =>
                        updateItem(index, "vendorRemark", e.target.value)
                      }
                    />
                  </td>
                </tr>
              ))}
            </tbody>

            <tfoot>
              <tr className="vr-total-row">
                <td colSpan="6" className="text-end fw-bold">
                  Total Amount
                </td>
                <td className="text-end fw-bold">{money(totalAmount)}</td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>

        <div className="vr-grand">
          <span>Grand Total</span>
          <span>{money(totalAmount)}</span>
        </div>

        {!submitted && (
          <div className="vr-submit-row text-end mt-3">
            <button className="btn btn-primary" onClick={submitRates}>
              Submit Rates
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default VendorRateForm;
